// @ts-check

import { uiAppName } from '@nova/core';

/**
 * @typedef HeliosEnvVars
 * @type {object}
 * @property {string} ui_app_name
 * @property {string} server_env
 * @property {string} neutron_url
 * @property {string} subspace_url
 * @property {string} luna_url
 * @property {string} mcp_url
 * @property {string} comet_url
 * @property {string} juno_url
 * @property {string} photon_url
 * @property {string} cat_url
 * @property {string} tc_last_update
 * @property {string} unleash_app_name
 * @property {string} unleash_url
 * @property {string} unleash_client_key
 * @property {string} [sentry_dsn]
 * @property {string} [sentry_traces_sample_rate]
 * @property {string} [datadog_rum_client_token]
 * @property {string} [datadog_rum_application_id]
 * @property {string} [datadog_rum_site]
 * @property {string} [datadog_rum_service]
 * @property {number} [datadog_rum_session_sample_rate]
 * @property {boolean} [datadog_rum_track_user_interactions]
 * @property {boolean} [datadog_rum_track_resources]
 * @property {boolean} [datadog_rum_track_long_tasks]
 * @property {number} [datadog_rum_session_replay_sample_rate]
 * @property {number} [datadog_rum_profiling_sample_rate]
 * @property {boolean} [datadog_rum_propagate_trace_baggage]
 * @property {string} [datadog_rum_default_privacy_level]
 * @property {string} [mixpanel_token]
 * Session Replay knobs are always set below, never env-driven — hence not optional.
 * @property {boolean} mixpanel_session_replay_enabled
 * @property {number} mixpanel_session_replay_sample_percent
 * @property {number} mixpanel_session_replay_max_ms
 * @property {number} mixpanel_session_replay_idle_timeout_ms
 * @property {string} [zendesk_key]
 * @property {string} [gtm_id]
 * @property {string} [beamer_product_id]
 * @property {string} [fullcalendar_key]
 */

import { DateTime } from 'luxon';

/**
 * @param {Partial<HeliosEnvVars>} env
 * @returns {string}
 */
function getUnleashUrl(env) {
  const { unleash_url, neutron_url } = env;
  const url = unleash_url ? unleash_url.trim() : null;
  if (url) {
    return url;
  }
  return new URL('/feature-flag/proxy', neutron_url).toString();
}

/**
 * @param {Partial<HeliosEnvVars>} env
 * @returns {string}
 */
function getUnleashClientKey(env) {
  const { unleash_client_key } = env;
  const key = unleash_client_key ? unleash_client_key.trim() : null;
  if (key) {
    return key;
  }
  const token =
    (sessionStorage?.getItem('access_token') || localStorage?.getItem('access_token')) ?? '';
  return `Bearer ${token || ''}`;
}

function getDatadogRumApplicationId(env) {
  const { datadog_rum_application_id } = env;
  return datadog_rum_application_id ? datadog_rum_application_id.trim() : '';
}

function getDatadogRumClientToken(env) {
  const { datadog_rum_client_token } = env;
  return datadog_rum_client_token ? datadog_rum_client_token.trim() : '';
}

/**
 * Session Replay is billed against ONE 20k-recordings/month pool shared by every Loadsmart
 * Mixpanel project, and Mixpanel gives us no UI-side control — so the rate lives here,
 * hardcoded, and cannot be switched on by an env var.
 *
 * Production only. 'staging' was enabled for the initial QA window (OD-2496) and removed
 * here (OD-3992): staging draws from the same 20k-recordings/month pool, and its Mixpanel
 * token is shared with Luna staging, so its replays land in a project we don't own.
 * Re-enable it only for a bounded QA window, and revert as soon as that window closes.
 */
const SESSION_REPLAY_ENVIRONMENTS = ['production'];

/** Production samples at 10%. Lower this if the shared pool projects over ~12k/month. */
/** @type {Record<string, number>} */
const SESSION_REPLAY_SAMPLE_PERCENT = { production: 10 };

const formattedVars = /** @type {HeliosEnvVars} */ (
  Object.entries(import.meta.env).reduce(
    (env, [key, value]) => ({ ...env, [key.replace('VITE_', '').toLowerCase()]: value }),
    {}
  )
);

/** @type {HeliosEnvVars} */
const config = {
  ...formattedVars,
  ...{
    ui_app_name: uiAppName.HELIOS,
    application_name: 'Opendock Nova',
    unleash_app_name: 'opendock-nova',
    unleash_url: getUnleashUrl(formattedVars),
    unleash_client_key: getUnleashClientKey(formattedVars),
    // NavTrac CAT = SmartGate annotation/workset admin tool (external).
    cat_url: (formattedVars.cat_url || 'https://cat.navtrac.com').replace(/\/+$/u, ''),
    terms_conditions_last_update: DateTime.fromISO(import.meta.env.VITE_TC_LAST_UPDATE),
    datadog_rum_application_id: getDatadogRumApplicationId(formattedVars),
    datadog_rum_client_token: getDatadogRumClientToken(formattedVars),
    datadog_rum_service: 'opendock-nova',
    datadog_rum_site: 'datadoghq.com',
    datadog_rum_session_sample_rate: 5,
    datadog_rum_track_user_interactions: true,
    datadog_rum_track_resources: false,
    datadog_rum_track_long_tasks: false,
    datadog_rum_session_replay_sample_rate: 0,
    datadog_rum_profiling_sample_rate: 0,
    datadog_rum_propagate_trace_baggage: false,
    datadog_rum_default_privacy_level: 'mask-user-input',
    // Mixpanel Session Replay — quota-protected knobs, intentionally not env-driven.
    mixpanel_session_replay_enabled: SESSION_REPLAY_ENVIRONMENTS.includes(formattedVars.server_env),
    mixpanel_session_replay_sample_percent:
      SESSION_REPLAY_SAMPLE_PERCENT[formattedVars.server_env] ?? 0,
    mixpanel_session_replay_max_ms: 3 * 60 * 60 * 1000, // 3 hours
    mixpanel_session_replay_idle_timeout_ms: 30 * 60 * 1000 // 30 minutes
  }
};

export default config;
